import { BusinessIDType, EntityType, PartyResponse } from 'compliance-api/generated';
import { FormikProvider, useFormik } from 'formik';
import { useCallback, useRef } from 'react';
import type { StylesConfig } from 'react-select';
import * as yup from 'yup';
import {
  businessNumberTypeOptions,
  entityTypeOptions,
} from '@/modules/admin-dashboard/components/contractManagement/partyOptions.ts';
import {
  useCreatePartyMutation,
  usePartyQuery,
  useUpdatePartyMutation,
} from '@/modules/admin-dashboard/queries/contract-management.ts';
import {
  BaseModalProps,
  Button,
  CustomCheckbox,
  ModalContentWrapper,
  Select,
  Spinner,
  TextInput,
  TextInputField,
} from 'ui';

type PartyFormValues = {
  id?: number;
  legal_name: string | null;
  trading_name: string | null;
  entity_type: EntityType | null;
  business_number_type: BusinessIDType | null;
  business_number: string | null;
  is_us: boolean;
  is_active: boolean;
};

interface PartyModalProps extends BaseModalProps {
  party?: PartyResponse;
  partyId?: number;
  onSuccess?: (party: PartyResponse) => void;
}

const businessNumberTypeSelectStyles: StylesConfig = {
  control: base => ({
    ...base,
    minHeight: '48px',
    height: '48px',
    alignItems: 'center',
    border: 0,
    boxShadow: 'none',
  }),
  valueContainer: base => ({
    ...base,
    height: '48px',
    paddingTop: 0,
    paddingBottom: 0,
    alignItems: 'center',
  }),
  placeholder: base => ({
    ...base,
    marginTop: 0,
    lineHeight: '22px',
  }),
  input: base => ({
    ...base,
    margin: 0,
  }),
  indicatorsContainer: base => ({
    ...base,
    height: '48px',
  }),
  dropdownIndicator: base => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  clearIndicator: base => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
};

const businessNumberValidationRules: Record<
  Exclude<BusinessIDType, BusinessIDType.Other>,
  { pattern: RegExp; message: string }
> = {
  [BusinessIDType.Abn]: {
    pattern: /^\d{11}$/,
    message: 'ABN must contain 11 digits',
  },
  [BusinessIDType.Acn]: {
    pattern: /^\d{9}$/,
    message: 'ACN must contain 9 digits',
  },
  [BusinessIDType.Tfn]: {
    pattern: /^\d{8,9}$/,
    message: 'TFN must contain 8 or 9 digits',
  },
};

const validationSchema = yup.object<PartyFormValues>({
  legal_name: yup
    .string()
    .nullable()
    .trim()
    .when('is_us', {
      is: true,
      then: schema => schema.notRequired(),
      otherwise: schema => schema.required('Legal name is required'),
    })
    .max(255),
  trading_name: yup
    .string()
    .nullable()
    .trim()
    .when('is_us', {
      is: true,
      then: schema => schema.notRequired(),
      otherwise: schema => schema.max(255),
    }),
  entity_type: yup
    .mixed<EntityType>()
    .nullable()
    .when('is_us', {
      is: true,
      then: schema => schema.notRequired(),
      otherwise: schema => schema.required('Entity type is required'),
    }),
  business_number_type: yup
    .mixed<BusinessIDType>()
    .nullable()
    .when('is_us', {
      is: true,
      then: schema => schema.notRequired(),
      otherwise: schema => schema.required('Business number type is required'),
    }),
  business_number: yup
    .string()
    .nullable()
    .trim()
    .when('is_us', {
      is: true,
      then: schema => schema.notRequired(),
      otherwise: schema => schema.required('Business number is required').max(64),
    })
    .test('business-number-type-format', function (value) {
      const { business_number_type, is_us } = this.parent as PartyFormValues;

      if (
        is_us ||
        !value ||
        !business_number_type ||
        business_number_type === BusinessIDType.Other
      ) {
        return true;
      }

      const rule = businessNumberValidationRules[business_number_type];
      const digitsOnlyValue = value.replace(/\s/g, '');

      if (rule.pattern.test(digitsOnlyValue)) {
        return true;
      }

      return this.createError({ message: rule.message });
    }),
  is_us: yup.boolean(),
});

const getInitialValues = (party?: PartyResponse): PartyFormValues => ({
  id: party?.id,
  legal_name: party?.legal_name || null,
  trading_name: party?.trading_name || null,
  entity_type: party?.entity_type || null,
  business_number_type: party?.business_number_type || null,
  business_number: party?.business_number || null,
  is_us: party?.is_us || false,
  is_active: party?.is_active ?? true,
});

const emptyBusinessValues: {
  legal_name: null;
  trading_name: null;
  entity_type: null;
  business_number_type: null;
  business_number: null;
} = {
  legal_name: null,
  trading_name: null,
  entity_type: null,
  business_number_type: null,
  business_number: null,
};

type BusinessValuesCache = {
  legal_name: string | null;
  trading_name: string | null;
  entity_type: EntityType | null;
  business_number_type: BusinessIDType | null;
  business_number: string | null;
};

const PartyModal = ({ party, partyId, open, onClose, onSuccess }: PartyModalProps) => {
  const shouldLoadParty = !!partyId && !party;
  const { data: partyData, isFetching: isPartyFetching } = usePartyQuery(
    partyId || 0,
    !shouldLoadParty,
  );
  const currentParty = party || partyData?.party;
  const isEditMode = !!currentParty?.id || !!partyId;
  const formik = useFormik<PartyFormValues>({
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    enableReinitialize: true,
    initialValues: getInitialValues(currentParty),
    onSubmit: async ({ id, ...values }) => {
      const normalizedValues = {
        ...values,
        legal_name: values.legal_name || undefined,
        trading_name: values.trading_name || undefined,
        entity_type: values.entity_type || undefined,
        business_number_type: values.business_number_type || undefined,
        business_number: values.business_number || undefined,
      };

      if (id) {
        const response = await updateParty({ id, data: normalizedValues });
        onSuccess?.(response.party);
        onClose();
        return;
      }

      const response = await createParty(normalizedValues);
      onSuccess?.(response.party);
      onClose();
    },
  });

  const { mutateAsync: createParty, isLoading: isCreateLoading } = useCreatePartyMutation(
    formik.setErrors,
  );
  const { mutateAsync: updateParty, isLoading: isUpdateLoading } = useUpdatePartyMutation(
    formik.setErrors,
  );

  const isLoading = isCreateLoading || isUpdateLoading;
  const isBusinessParty = formik.values.is_us;
  const businessValuesRef = useRef<BusinessValuesCache | null>(null);

  const handleChangeSelect = useCallback(
    (field: keyof PartyFormValues) => async (value: unknown) => {
      await formik.setFieldValue(field, value);
      formik.setFieldError(field, '');
    },
    [formik],
  );

  const handleChangeBusinessNumberType = useCallback(
    async (value: unknown) => {
      await formik.setFieldValue('business_number_type', value);
      formik.setFieldError('business_number_type', '');
      formik.setFieldError('business_number', '');
    },
    [formik],
  );

  const handleChangeBusinessParty = useCallback(
    async (checked: boolean) => {
      await formik.setFieldValue('is_us', checked);
      formik.setFieldError('is_us', '');

      if (checked) {
        businessValuesRef.current = {
          legal_name: formik.values.legal_name,
          trading_name: formik.values.trading_name,
          entity_type: formik.values.entity_type,
          business_number_type: formik.values.business_number_type,
          business_number: formik.values.business_number,
        };

        formik.setValues(values => ({
          ...values,
          ...emptyBusinessValues,
        }));
        return;
      }

      if (businessValuesRef.current) {
        formik.setValues(values => ({
          ...values,
          ...businessValuesRef.current,
        }));
      }
    },
    [formik],
  );

  return (
    <ModalContentWrapper
      title={isEditMode ? 'Edit party' : 'Add new party'}
      open={open}
      onClose={onClose}
      classes={{ dialogPanel: 'max-w-[502px]' }}
    >
      {shouldLoadParty && isPartyFetching ? (
        <div className="flex min-h-[240px] items-center justify-center">
          <Spinner size="lg" variant="primary" />
        </div>
      ) : (
        <FormikProvider value={formik}>
          <div className="space-y-8">
            <TextInputField name="legal_name" label="Legal name" disabled={isBusinessParty} />

            <TextInputField
              name="trading_name"
              label="Trading name (optional)"
              disabled={isBusinessParty}
            />

            <Select
              label="Entity type"
              options={entityTypeOptions}
              value={formik.values.entity_type}
              onChange={handleChangeSelect('entity_type')}
              isClearable
              showBorderBottom
              disabled={isBusinessParty}
              error={formik.errors.entity_type}
            />

            <div>
              <span className="font-[Inter] text-[10px] leading-[18px] text-gray-900 text-opacity-[.64] uppercase">
                Business number
              </span>
              <div className="flex items-start gap-4 border-b border-gray-900 border-opacity-16 focus-within:border-opacity-100">
                <Select
                  options={businessNumberTypeOptions}
                  value={formik.values.business_number_type}
                  onChange={handleChangeBusinessNumberType}
                  className="w-fit shrink-0"
                  selectStyles={businessNumberTypeSelectStyles}
                  isClearable
                  showBorderBottom={false}
                  disabled={isBusinessParty}
                />
                <TextInput
                  name="business_number"
                  className="min-w-0 !border-0 py-[14px] "
                  placeholder="Number"
                  disabled={isBusinessParty}
                  value={formik.values.business_number || ''}
                  onChange={e => {
                    formik.setFieldValue(e.target.name, e.target.value);
                    formik.setFieldError(e.target.name, '');
                  }}
                />
              </div>
              {(formik.errors.business_number_type || formik.errors.business_number) && (
                <p className="mt-1 text-sm text-[12px] text-error-red">
                  {formik.errors.business_number_type
                    ? formik.errors.business_number_type?.[0].toUpperCase() +
                      formik.errors.business_number_type?.substring(1)
                    : formik.errors.business_number
                    ? formik.errors.business_number?.[0].toUpperCase() +
                      formik.errors.business_number?.substring(1)
                    : ''}
                </p>
              )}
            </div>

            {!isEditMode && (
              <CustomCheckbox
                label="Is this party your business"
                checked={formik.values.is_us}
                onChange={event => void handleChangeBusinessParty(event.target.checked)}
              />
            )}

            <Button disabled={isLoading} loading={isLoading} onClick={formik.submitForm}>
              Confirm
            </Button>
          </div>
        </FormikProvider>
      )}
    </ModalContentWrapper>
  );
};

export default PartyModal;
